import {
  PracticeSetManagedResponse,
  PracticeUpdateManagedAffiliatesRequest,
  ProviderAddMoveResponse,
  ProviderRemovalResponse,
  ResetInviteResponse,
  StatusUpdateResult,
} from "../../../domain/Practice/schema"
import { ManagedProviderResponse } from "../../../domain/Provider/schema"
import { ApiError } from "../ApiError"
import { buildUrl, getData, postData, putData } from "../apiService"
import {
  ReferralSessionSummary,
  ReferralSessionWrite,
} from "../../../domain/Referral/schema"

export interface LegacyDto {
  legacyAffiliateId?: number
  legacyLocationId?: number
}

const apiRoutes = {
  account: {
    GET_B2C_USER_BY_ID: "/account/b2c/users/:b2cObjectId",
    UPDATE_USER_EMAIL: "/account/users/:userId/email",
    UPDATE_B2C_EMAIL: "/account/b2c/users/:b2cObjectId/email",
    RESET_B2C_PASSWORD: "/account/b2c/users/:b2cObjectId/reset-password",
    RESET_B2C_PHONE_MFA: "/account/b2c/users/:b2cObjectId/reset-phone-mfa",
    RESET_B2C_APP_MFA: "/account/b2c/users/:b2cObjectId/reset-totp-mfa",
    SUPPORT_CLEAR_ONBOARDING_STEPS: "/account/support/resetOnboarding/:userId",
  },
  practice: {
    PRACTICE_SUPPORT_UPDATE_LEGACY_AFFILIATE_ID_ROUTE:
      "/practice/support/:practiceId/updatelegacyaffiliateid",
    PRACTICE_SUPPORT_SWAP_LEGACY_ID_ROUTE:
      "/practice/support/:practiceId/swapLegacyId/:newLegacyId",
    PRACTICE_SUPPORT_ARCHIVE_ROUTE: "/practice/support/:practiceId/archive",
    PRACTICE_SUPPORT_UNARCHIVE_ROUTE: "/practice/support/:practiceId/unarchive",
    PRACTICE_SUPPORT_SET_MANAGED_ROUTE:
      "/practice/support/:practiceId/convertToManaged",
    PRACTICE_SUPPORT_UPDATE_MANAGED_AFFILIATES_ROUTE:
      "/practice/support/updateManagedAffiliateStatuses",
    PRACTICE_SUPPORT_RESET_INVITE_ROUTE:
      "/practice/support/resetInvite/:inviteCode",
    PRACTICE_SUPPORT_REMOVE_PROVIDER_ROUTE:
      "/practice/support/:practiceId/:affiliateId/removeProviderRole",
    PRACTICE_SUPPORT_ADD_MOVE_PROVIDER_ROLE_ROUTE:
      "/practice/support/:practiceId/:affiliateId/addProviderRole",
  },
  provider: {
    PROVIDER_SUPPORT_ARCHIVE_ROUTE: "/provider/support/:providerId/archive",
    PROVIDER_SUPPORT_UNARCHIVE_ROUTE: "/provider/support/:providerId/unarchive",
    PROVIDER_SUPPORT_CLEAR_USER_ROUTE:
      "/provider/support/:providerId/clearUserAssociation/",
    PROVIDER_SUPPORT_SET_USER_ROUTE:
      "/provider/support/:providerId/addUserAssociation/",
    PROVIDERS_SUPPORT_SET_MANAGED: "/provider/support/convertToManaged",
    PROVIDER_SUPPORT_CLEAR_LIABILITY_ROUTE:
      "/provider/support/:providerId/clearLiability",
    PROVIDER_SUPPORT_CLEAR_DISCLOSURE_ROUTE:
      "/provider/support/:providerId/clearDisclosure",
  },
  referral: {
    SUPPORT_RESEND_REFERRAL_MESSAGE_ROUTE:
      "/referral/support/:referralId/syncReferral",
    SUPPORT_RESEND_SESSION_MESSAGE_ROUTE:
      "/referral/support/:sessionId/syncSession",
    SUPPORT_PUSH_SESSION_ROUTE: "/referral/support/:referralId/pushSession",
  },
  payment: {
    SUPPORT_RESEND_INVOICE_MESSAGE_ROUTE:
      "/payment/support/:paymentId/syncInvoice",
  },
}

const postPracticeSupportUpdateLegacyAffiliateId = async (
  practiceId: string,
  legacyDto: LegacyDto,
) => {
  return postData<void>(
    buildUrl(
      apiRoutes.practice.PRACTICE_SUPPORT_UPDATE_LEGACY_AFFILIATE_ID_ROUTE,
      { practiceId },
    ),
    legacyDto,
  )
}

const postPracticeSupportArchive = async (practiceId: string) => {
  return postData<void>(
    buildUrl(apiRoutes.practice.PRACTICE_SUPPORT_ARCHIVE_ROUTE, { practiceId }),
    {},
  )
}

const postPracticeSupportSwapLegacyId = async (
  practiceId: string,
  newLegacyId: string | number,
) => {
  return postData<void>(
    buildUrl(apiRoutes.practice.PRACTICE_SUPPORT_SWAP_LEGACY_ID_ROUTE, {
      practiceId,
      newLegacyId: newLegacyId.toString(),
    }),
    {},
  )
}

const postPracticeSupportUnarchive = async (practiceId: string) => {
  return postData<void>(
    buildUrl(apiRoutes.practice.PRACTICE_SUPPORT_UNARCHIVE_ROUTE, {
      practiceId,
    }),
    {},
  )
}

const convertToManaged = async (
  practiceId: string,
): Promise<PracticeSetManagedResponse | undefined> => {
  try {
    const result = await postData<PracticeSetManagedResponse>(
      buildUrl(apiRoutes.practice.PRACTICE_SUPPORT_SET_MANAGED_ROUTE, {
        practiceId,
      }),
      {},
    )
    return result as PracticeSetManagedResponse
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.statusCode === 409) {
        if (
          error.errorDetails &&
          error.errorDetails.data === "Practice is already managed!"
        ) {
          throw new Error("Practice is already managed!")
        }
        if (
          error.errorDetails &&
          error.errorDetails.data ===
            "Practice must have active group liability insurance to become managed!"
        ) {
          throw new Error(
            "Practice must have active group liability insurance to become managed!",
          )
        }
      }
    }
    return undefined
  }
}

const updateManagedAffiliateStatuses = async (
  affiliatesToUpdate: PracticeUpdateManagedAffiliatesRequest,
) => {
  const result = await postData<StatusUpdateResult>(
    apiRoutes.practice.PRACTICE_SUPPORT_UPDATE_MANAGED_AFFILIATES_ROUTE,
    affiliatesToUpdate,
  )
  return result as StatusUpdateResult
}

const removeProviderRole = async (practiceId: string, affiliateId: string) => {
  const result = await postData(
    buildUrl(apiRoutes.practice.PRACTICE_SUPPORT_REMOVE_PROVIDER_ROUTE, {
      practiceId,
      affiliateId,
    }),
    {},
  )
  return result as ProviderRemovalResponse
}

const addMoveProviderRole = async (
  practiceId: string,
  affiliateId: string,
  providerId?: string,
) => {
  const result = await postData(
    buildUrl(apiRoutes.practice.PRACTICE_SUPPORT_ADD_MOVE_PROVIDER_ROLE_ROUTE, {
      practiceId,
      affiliateId,
    }),
    { providerId: providerId ? providerId : undefined },
  )
  return result as ProviderAddMoveResponse
}

const providersSetManaged = async (providerIds: string[]) => {
  const result = await postData<ManagedProviderResponse>(
    apiRoutes.provider.PROVIDERS_SUPPORT_SET_MANAGED,
    { providerIds },
  )
  return result as ManagedProviderResponse
}

// B2C
export type B2CUserReturn = {
  objectId: string
  displayName: string
  email: string
  hasPhoneMfa: boolean
  hasTotpMfa: boolean
  mfaPhoneNumber: string
}

const getB2CUserById = async ({ userId }: { userId: string }) => {
  return getData<B2CUserReturn>(
    buildUrl(apiRoutes.account.GET_B2C_USER_BY_ID, { b2cObjectId: userId }),
  )
}

const updateUserEmail = async ({
  userId,
  newEmail,
}: {
  userId: string
  newEmail: string
}) => {
  return putData(buildUrl(apiRoutes.account.UPDATE_USER_EMAIL, { userId }), {
    email: newEmail,
  })
}

type B2CActionResponse = {
  success: boolean
  message: string | null
  errorMessage: string | null
}

const updateB2CEmail = async ({
  userId,
  newEmail,
}: {
  userId: string
  newEmail: string
}) => {
  return putData<B2CActionResponse>(
    buildUrl(apiRoutes.account.UPDATE_B2C_EMAIL, { b2cObjectId: userId }),
    { newEmail },
  )
}

const resetB2CPhoneMFA = async ({
  userId,
  newPhoneNumber,
}: {
  userId: string
  newPhoneNumber: string
}) => {
  return postData<B2CActionResponse>(
    buildUrl(apiRoutes.account.RESET_B2C_PHONE_MFA, { b2cObjectId: userId }),
    { newPhoneNumber, phoneType: "mobile" },
  )
}

const resetB2CAppMFA = async ({ userId }: { userId: string }) => {
  return postData<B2CActionResponse>(
    buildUrl(apiRoutes.account.RESET_B2C_APP_MFA, { b2cObjectId: userId }),
  )
}

type B2CPasswordResetResponse = {
  success: boolean
  generatedPassword: string | null
  forceChangePasswordNextSignIn: boolean
  errorMessage: string | null
}

const resetPassword = async ({
  userId,
  newPassword,
}: {
  userId: string
  newPassword?: string
}) => {
  return postData<B2CPasswordResetResponse>(
    buildUrl(apiRoutes.account.RESET_B2C_PASSWORD, { b2cObjectId: userId }),
    { newPassword, forceChangePasswordNextSignIn: true },
  )
}

const resetOnboardingSteps = async ({ userId }: { userId: string }) => {
  return postData(
    buildUrl(apiRoutes.account.SUPPORT_CLEAR_ONBOARDING_STEPS, { userId }),
  )
}

const resetInvite = async (inviteCode: string) => {
  return postData<ResetInviteResponse>(
    buildUrl(apiRoutes.practice.PRACTICE_SUPPORT_RESET_INVITE_ROUTE, {
      inviteCode,
    }),
    {},
  )
}

const archiveProvider = async (providerId: string) => {
  return postData(
    buildUrl(apiRoutes.provider.PROVIDER_SUPPORT_ARCHIVE_ROUTE, { providerId }),
    {},
  )
}

const unarchiveProvider = async (providerId: string) => {
  return postData(
    buildUrl(apiRoutes.provider.PROVIDER_SUPPORT_UNARCHIVE_ROUTE, {
      providerId,
    }),
    {},
  )
}

const clearProviderUserAssociation = async (providerId: string) => {
  return postData(
    buildUrl(apiRoutes.provider.PROVIDER_SUPPORT_CLEAR_USER_ROUTE, {
      providerId,
    }),
    {},
  )
}

const setProviderUserAssociation = async (
  providerId: string,
  userId: string,
) => {
  return postData(
    buildUrl(apiRoutes.provider.PROVIDER_SUPPORT_SET_USER_ROUTE, {
      providerId,
    }),
    { userId },
  )
}

const clearProviderLiabilityInsurance = async (providerId: string) => {
  return postData(
    buildUrl(apiRoutes.provider.PROVIDER_SUPPORT_CLEAR_LIABILITY_ROUTE, {
      providerId,
    }),
    {},
  )
}

const clearProviderDisclosure = async (providerId: string) => {
  return postData(
    buildUrl(apiRoutes.provider.PROVIDER_SUPPORT_CLEAR_DISCLOSURE_ROUTE, {
      providerId,
    }),
    {},
  )
}

const syncReferral = async (referralId: string) => {
  return postData(
    buildUrl(apiRoutes.referral.SUPPORT_RESEND_REFERRAL_MESSAGE_ROUTE, {
      referralId,
    }),
    {},
  )
}

const syncSession = async (sessionId: string) => {
  return postData(
    buildUrl(apiRoutes.referral.SUPPORT_RESEND_SESSION_MESSAGE_ROUTE, {
      sessionId,
    }),
    {},
  )
}

const pushSession = async (
  referralId: string,
  sessionData: ReferralSessionWrite,
) => {
  return postData<ReferralSessionSummary>(
    buildUrl(apiRoutes.referral.SUPPORT_PUSH_SESSION_ROUTE, { referralId }),
    sessionData,
  )
}

const syncInvoice = async (paymentId: string) => {
  return postData(
    buildUrl(apiRoutes.payment.SUPPORT_RESEND_INVOICE_MESSAGE_ROUTE, {
      paymentId,
    }),
    {},
  )
}

export const supportApi = {
  account: {
    GET_B2C_USER_BY_ID: getB2CUserById,
    UPDATE_USER_EMAIL: updateUserEmail,
    UPDATE_B2C_EMAIL: updateB2CEmail,
    RESET_B2C_PASSWORD: resetPassword,
    RESET_B2C_PHONE_MFA: resetB2CPhoneMFA,
    RESET_B2C_APP_MFA: resetB2CAppMFA,
    RESET_ONBOARDING_STEPS: resetOnboardingSteps,
  },
  practice: {
    UPDATE_LEGACY_AFFILIATE_ID: postPracticeSupportUpdateLegacyAffiliateId,
    SWAP_LEGACY_ID: postPracticeSupportSwapLegacyId,
    ARCHIVE_PRACTICE: postPracticeSupportArchive,
    UNARCHIVE_PRACTICE: postPracticeSupportUnarchive,
    CONVERT_TO_MANAGED: convertToManaged,
    UPDATE_MANAGED_AFFILIATE_STATUSES: updateManagedAffiliateStatuses,
    RESET_INVITE: resetInvite,
    REMOVE_PROVIDER_ROLE: removeProviderRole,
    ADD_MOVE_PROVIDER_ROLE: addMoveProviderRole,
  },
  provider: {
    PROVIDERS_SET_MANAGED: providersSetManaged,
    ARCHIVE_PROVIDER: archiveProvider,
    UNARCHIVE_PROVIDER: unarchiveProvider,
    CLEAR_PROVIDER_USER_ASSOCIATION: clearProviderUserAssociation,
    SET_PROVIDER_USER_ASSOCIATION: setProviderUserAssociation,
    CLEAR_PROVIDER_LIABILITY_INSURANCE: clearProviderLiabilityInsurance,
    CLEAR_PROVIDER_DISCLOSURE: clearProviderDisclosure,
  },
  referral: {
    SYNC_REFERRAL: syncReferral,
    SYNC_SESSION: syncSession,
    PUSH_SESSION: pushSession,
  },
  payment: {
    SYNC_INVOICE: syncInvoice,
  },
}
